import { EventEmitter, Injectable, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { AlertController, IonicSafeString } from '@ionic/angular';

import { AppSettings } from '../../app.settings';
import { ApplicationUser } from '../models/ApplicationUser';
import { IBottleLoading } from '../models/Error';
import { DeviceService } from './device/device.service';
import { FcmTokenService } from './fcm/fcm-token.service';
import { StorageData } from './utilities/storage-data.class';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  @Output() validated = new EventEmitter<boolean>()

  public spinner = false
  public isEnabled = false

  private alert: HTMLIonAlertElement | null = null;

  constructor(
    private alertC: AlertController,
    private deviceS: DeviceService,
    private readonly fcmTokenS: FcmTokenService
  ) { }

  public getCurrentUser = async () => await StorageData.getDataStorage(AppSettings.TOKEN_KEY);

  public getProfileUser = async () => await StorageData.getDataStorage(AppSettings.PERFIL_USUARIO);

  public presentAlert = async (loading: IBottleLoading) => {
    if (this.alert !== null) this.alert.dismiss();
    this.alert = await this.alertC.create({
      animated: true,
      backdropDismiss: loading.showBackdrop,
      keyboardClose: false,
      translucent: true,
      mode: 'ios',
      cssClass: loading.cssClass,
      message: new IonicSafeString(loading.message).value,
      buttons: [
        {
          text: loading.spinner,
          handler: () => this.alert?.dismiss(true)
        }
      ]
    })
    await this.alert.present();

    const { data } = await this.alert.onDidDismiss();
    return data;
  }

  public alertDismiss(): void {
    if (this.alert !== null) {
      this.alert.dismiss()
    }
  }

  public getDeviceId = async () => (await Device.getId()).identifier;

  public getUuId = (): string => {
    const u = Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
    return [u.substring(0, 8), u.substring(8, 12), '4000-8' + u.substring(13, 16), u.substring(16, 28)].join('-');
  }

  public updateDeviceUserData = async (): Promise<ApplicationUser> => {
    let dataUser: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY) as ApplicationUser;
    const dataDevice = await Device.getInfo();

    if (!dataUser || !dataUser.uniqueDeviceID || !dataUser.deviceInfo) {
      let deviceID = '';
      let device = '';
      let tokenFirebase = '';

      const platformDevice = dataDevice.platform ?? 'unknown-platform';
      let versionDevice = await this.deviceS.getFullOSVersion();
      versionDevice = versionDevice === 'unknown-version' ? '' : versionDevice;

      const manucaturerDevice = dataDevice.manufacturer ?? 'uknown-manufacturer';
      const modelDevice = dataDevice.model ?? 'uknown-model';

      if (!Capacitor.isNativePlatform()) {
        deviceID = this.getUuId();
        device = `${dataDevice.platform}, ${window.navigator.platform}`;
      } else if (dataDevice.platform === 'android') {
        try {
          deviceID = await this.getDeviceId();
        } catch (error) {
          /*
            Para Android, versionDevice es el nivel de API (SDK version) Android 10 corresponde al nivel de API 29
            Así que verificamos si el SDK es >= 29 (Android 10 o superior)
          */
          const versionNumber = typeof versionDevice === 'string' ?
            parseInt(versionDevice, 10) :
            Number(versionDevice);

          if (!isNaN(versionNumber) && versionNumber >= 29) {
            const uuid = await this.getDeviceId();
            deviceID = `00000000-0000-0000-${uuid.slice(0, 4)}-${uuid.slice(4, uuid.length)}`;
          }
        }
        device = `${manucaturerDevice} ${modelDevice} ${platformDevice} ${versionDevice}`;
        tokenFirebase = await this.fcmTokenS.getToken();
      } else {
        deviceID = await this.getDeviceId();
        device = `${manucaturerDevice} ${modelDevice} ${platformDevice} ${versionDevice}`;
        tokenFirebase = await this.fcmTokenS.getToken();
      }
      deviceID = String(deviceID).length < 5 ? this.getUuId() : deviceID;

      dataUser = {
        token: tokenFirebase
        , uniqueDeviceID: deviceID
        , platform: platformDevice === 'web' ? 'browser' : platformDevice
        , create_date: new Date()
        , deviceInfo: device
      };

      StorageData.setDataStorage(AppSettings.TOKEN_KEY, dataUser);
      localStorage.setItem(AppSettings.TOKEN_KEY, JSON.stringify(dataUser));

      if (!Capacitor.isNativePlatform()) {
        tokenFirebase = await this.fcmTokenS.getToken();
        dataUser = { ...dataUser, token: tokenFirebase };
        StorageData.setDataStorage(AppSettings.TOKEN_KEY, dataUser);
        localStorage.setItem(AppSettings.TOKEN_KEY, JSON.stringify(dataUser));
      }
    }

    if (!localStorage.getItem(AppSettings.TOKEN_KEY) || dataUser.uniqueDeviceID !== JSON.parse(localStorage.getItem(AppSettings.TOKEN_KEY) || '{}').uniqueDeviceID) {
      localStorage.setItem(AppSettings.TOKEN_KEY, JSON.stringify(dataUser));
    }

    return dataUser
  }

  public MatchPassword(password: string, confirmPassword: string) {
    return (formGroup: UntypedFormGroup) => {
      const passwordControl = formGroup.controls[password];
      const confirmPasswordControl = formGroup.controls[confirmPassword];

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (confirmPasswordControl.errors && !confirmPasswordControl.errors.passwordMismatch) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
      } else {
        confirmPasswordControl.setErrors(null);
      }
    };
  }

  /**
   * Genera un jwt local en la app, para autentificación del mismo
   * @param ID_Bodega 
   * @param NUD 
   */
  public generatedAppJWT = (ID_Bodega: number, NUD: number) => {
    const obj = { ID_Bodega, NUD, iat: new Date().getTime(), exp: new Date(new Date().setFullYear(new Date().getFullYear() + 40)) };
    return btoa(JSON.stringify(obj));
  }


  /**
 * Verifica el estado actual del permiso de notificación y lo almacena tanto localmente como en el servidor.
 */
  public async checkAndStoreNotificationPermission(forcePermissionSending?: boolean): Promise<void> {
    try {
      let dataDeviceUser: ApplicationUser = await this.getCurrentUser();
      // Obtiene permiso de notificación usando la API de Capacitor
      const hasNotificationPermission = await this.fcmTokenS.hasPermission();

      // Verifica si el permiso ya fue verificado y el valor no ha cambiado
      if (!forcePermissionSending && dataDeviceUser?.notification_permission_granted === hasNotificationPermission) {
        await this.fcmTokenS.refreshIfNeeded();
        return;
      }

      if (dataDeviceUser) {
        const getProfileUser = await this.getProfileUser();
        const now = new Date();

        dataDeviceUser = {
          ...dataDeviceUser,
          notification_permission_granted: hasNotificationPermission,
          token_last_refresh: now
        };

        // Actualiza si el usuario tiene permiso de notificación en el almacenamiento local
        await StorageData.setDataStorage(AppSettings.TOKEN_KEY, dataDeviceUser);
        localStorage.setItem(AppSettings.TOKEN_KEY, JSON.stringify(dataDeviceUser));

        await this.fcmTokenS.syncPushPermissions(
          {
            ID_Tendero: getProfileUser && getProfileUser.usr_idTendero ? getProfileUser.usr_idTendero : 0,
            DeviceID: dataDeviceUser.uniqueDeviceID,
            DeviceIfo: dataDeviceUser.deviceInfo,
            PushActivo: hasNotificationPermission
          },
          true
        );

        await this.fcmTokenS.syncFcmTokenWithBackend();
      }
    } catch (error) {
      console.error('Error al verificar permisos de notificación:', error);
    }
  }

}